var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { StyleParamType } from '@wix/tpa-settings';
import { isWixThemeVariable } from '@wix/wix-site-theme-variables';
import { generateTPAParamsWithMetadata, getSiteLevelStyles } from './generateTPAParams';
import { getProcessor } from './processor';
import { cssFunctions, toRgbParts } from './cssFunctions';
import { defaultOptions, plugins } from './common';
import { wixStylesFontUtils } from './utils/wixStyleFontUtils';
import { emitCssVariable, isColorTypeThemeVariable, isTextFontThemeVariable, isGlobalColor, isGlobalFont, toStylesheet, isColorStyleType, addColorSubVariables, addFontSubVariables, remapThemeFontLineHeightKey, } from './css-variables.utils';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';
var prefixes = {
    siteColors: 'wix',
    textPresets: 'wix-font', // --wix-font-{name}
};
export function getComponentCssVariables(styles, customExpressions, cssVars, customOptions, defaults, customCssVars, tpaData, siteThemeVars) {
    var cssVariables = {};
    var options = __assign(__assign({}, defaultOptions), (customOptions || {}));
    var _a = generateTPAParamsWithMetadata({
        siteColors: styles.siteColors,
        siteTextPresets: styles.siteTextPresets,
        styleParams: styles.styleParams,
        options: options,
        defaults: defaults,
        siteThemeVars: siteThemeVars,
    }), tpaParams = _a.tpaParams, styleParamsWithMetadataDefaults = _a.styleParamsWithMetadataDefaults;
    // globals
    cssVariables['--wix-direction'] = options.isRTL ? 'rtl' : 'ltr';
    // numbers
    Object.entries(tpaParams.numbers).forEach(function (_a) {
        var key = _a[0], value = _a[1];
        var cssVariable = emitCssVariable(key, cssFunctions.number(value));
        cssVariables[cssVariable.key] = cssVariable.value;
    });
    // strings
    Object.entries(tpaParams.strings).forEach(function (_a) {
        var key = _a[0], font = _a[1];
        var value = typeof font === 'string' ? font : font.value;
        var cssVariable = emitCssVariable(key, value);
        cssVariables[cssVariable.key] = cssVariable.value;
    });
    // colors
    var colorKeys = Object.keys(defaults !== null && defaults !== void 0 ? defaults : {})
        .filter(function (key) { return defaults[key].type === StyleParamType.Color; })
        .map(function (key) { return defaults[key].key || key; });
    Object.entries(tpaParams.colors)
        .filter(function (_a) {
        var key = _a[0];
        return styles.styleParams.colors[key] || colorKeys.includes(key);
    })
        .forEach(function (_a) {
        var key = _a[0], color = _a[1];
        try {
            var rgbParts = toRgbParts(cssFunctions.color(color, tpaParams));
            var cssVariable = emitCssVariable(key, rgbParts.full);
            cssVariables[cssVariable.key] = cssVariable.value;
            addColorSubVariables({ cssVariable: cssVariable, rgbParts: rgbParts }, cssVariables);
        }
        catch (e) {
            // swallow
        }
    });
    // fonts
    Object.entries(tpaParams.fonts)
        .filter(function (_a) {
        var key = _a[0];
        return !isGlobalFont(styles.siteTextPresets, key);
    })
        .forEach(function (_a) {
        var key = _a[0], font = _a[1];
        try {
            var cssVariable = emitCssVariable(key, cssFunctions.font(font, tpaParams));
            cssVariables[cssVariable.key] = cssVariable.value;
            addFontSubVariables({ cssVariable: cssVariable, font: font, addTextDecoration: true }, cssVariables);
        }
        catch (e) {
            // swallow
        }
    });
    // custom expressions
    var processor = getProcessor({ cssVars: cssVars, plugins: plugins });
    Object.entries(customExpressions).forEach(function (_a) {
        var key = _a[0], expression = _a[1];
        try {
            var value = processor.process({
                part: expression,
                tpaParams: tpaParams,
            });
            var cssVariable = emitCssVariable(key, value);
            cssVariables[cssVariable.key] = cssVariable.value;
        }
        catch (e) {
            // swallow
        }
    });
    if (typeof customCssVars === 'function') {
        var customCssVarsValue = customCssVars({
            styleParams: tpaParams,
            isMobile: options.isMobile,
            isRTL: options.isRTL,
            tpaData: tpaData,
        });
        Object.entries(customCssVarsValue).forEach(function (_a) {
            var key = _a[0], value = _a[1];
            var cssVariable = emitCssVariable(key, String(value));
            cssVariables[cssVariable.key] = cssVariable.value;
        });
    }
    Object.assign(cssVariables, getStyleParamCssVariables(styleParamsWithMetadataDefaults));
    return {
        variables: cssVariables,
        stylesheet: toStylesheet(cssVariables),
    };
}
export function getSiteCssVariables(siteColors, siteTextPresets, siteThemeVars) {
    var cssVariables = siteThemeVars ? getThemeCssVariables(siteThemeVars) : {};
    var emptyTpaParams = {
        booleans: {},
        colors: {},
        fonts: {},
        numbers: {},
        strings: {},
    };
    var _a = getSiteLevelStyles(siteColors, siteTextPresets), colors = _a.colors, fonts = _a.fonts;
    // colors
    Object.entries(colors)
        .filter(function (_a) {
        var key = _a[0];
        return isGlobalColor(key);
    })
        .forEach(function (_a) {
        var key = _a[0], color = _a[1];
        var rgbParts = toRgbParts(cssFunctions.color(color, emptyTpaParams));
        var cssVariable = emitCssVariable(key, rgbParts.full, prefixes.siteColors);
        cssVariables[cssVariable.key] = cssVariable.value;
    });
    // fonts
    Object.entries(fonts)
        .filter(function (_a) {
        var key = _a[0];
        return isGlobalFont(siteTextPresets, key);
    })
        .forEach(function (_a) {
        var key = _a[0], font = _a[1];
        var cssVariable = emitCssVariable(key, cssFunctions.font(font, emptyTpaParams), prefixes.textPresets);
        cssVariables[cssVariable.key] = cssVariable.value;
        addFontSubVariables({ cssVariable: cssVariable, font: font, addTextDecoration: true }, cssVariables);
    });
    return {
        variables: cssVariables,
        stylesheet: toStylesheet(cssVariables),
    };
}
export var getStyleParamCssVariables = function (styleParamsWithMetadataDefaults) {
    var result = {};
    Object.entries(styleParamsWithMetadataDefaults).forEach(function (_a) {
        var key = _a[0], value = _a[1];
        if (value.type === StyleType.font) {
            try {
                var parsedFont = wixStylesFontUtils.parseFontFromString(value.cssValue);
                var cssVariable = emitCssVariable(key, value.cssValue);
                result[cssVariable.key] = cssVariable.value;
                if (parsedFont && !('system' in parsedFont)) {
                    addFontSubVariables({ cssVariable: cssVariable, font: parsedFont }, result);
                }
            }
            catch (e) { }
        }
        else if (isColorStyleType(value.type)) {
            try {
                var rgbParts = toRgbParts(value.cssValue);
                var cssVariable = emitCssVariable(key, rgbParts.full);
                result[cssVariable.key] = cssVariable.value;
                addColorSubVariables({ cssVariable: cssVariable, rgbParts: rgbParts }, result);
            }
            catch (e) { }
        }
        else {
            try {
                var cssVariable = emitCssVariable(key, value.cssValue);
                result[cssVariable.key] = cssVariable.value;
            }
            catch (e) { }
        }
    });
    return result;
};
export var getThemeCssVariables = function (themeVariables) {
    var result = {};
    Object.entries(themeVariables).forEach(function (_a) {
        var key = _a[0], value = _a[1];
        if (!isWixThemeVariable(key)) {
            return;
        }
        if (isColorTypeThemeVariable(key)) {
            try {
                var rgbParts = toRgbParts(value);
                var cssVariable = emitCssVariable(key, rgbParts.full);
                result[cssVariable.key] = cssVariable.value;
                addColorSubVariables({ cssVariable: cssVariable, rgbParts: rgbParts }, result);
            }
            catch (e) { }
        }
        else if (isTextFontThemeVariable(key)) {
            try {
                var parsedFont = wixStylesFontUtils.parseFontFromString(value);
                if (parsedFont && !('system' in parsedFont)) {
                    var cssVariable = emitCssVariable(key, value);
                    var temp = {};
                    result[cssVariable.key] = cssVariable.value;
                    addFontSubVariables({ cssVariable: cssVariable, font: parsedFont }, temp);
                    remapThemeFontLineHeightKey(cssVariable.key, temp);
                    Object.assign(result, temp);
                }
            }
            catch (e) { }
        }
        else {
            try {
                var cssVariable = emitCssVariable(key, value);
                result[cssVariable.key] = cssVariable.value;
            }
            catch (e) { }
        }
    });
    return result;
};
//# sourceMappingURL=css-variables.js.map